import {
  BarChart3,
  BookOpen,
  FilePenLine,
  CalendarDays,
  Home,
  Trophy,
  NotebookPen,
  MessageSquare,
  Star,
  type LucideIcon,
} from "lucide-react";

/**
 * 마이페이지(/my) 통합 대시보드 네비게이션 SSOT (JCG-2073).
 *
 * 기존에 Clerk UserButton 팝오버(CustomUserButton)와 개별 라우트(/my/*)로 흩어져 있던 7개
 * 사용자 메뉴 항목을 단일 데이터로 정의한다. 소비처:
 *  - `src/app/my/MyLayoutClient.tsx` — 좌측 탭 네비(데스크탑 셸) + 모바일 세그먼트 바
 *  - `src/components/CustomUserButton.tsx` — 모바일 사용자 메뉴 대시보드 + 탭 라우트 링크
 *  - `src/components/article-shell/IconRail.tsx` — 데스크탑 셸 "내 메뉴" 드로어
 *
 * 감자 이용권·구매 목록은 설정으로 이동했다(JCG-3696). 기존 주소는 설정을 여는 호환 경로로 유지한다.
 * 기본 탭을 추가하면 여기에 추가한다. `/my/refund-request`는 직접 경로와 보조 UI만 유지하며
 * 공통 메뉴에는 노출하지 않는다(JCG-3402).
 * 상단바 제목은 `route-layout.ts` 의 `MY_DASHBOARD_LABELS` 와 쌍을 맞출 것.
 */
export interface MyNavItem {
  /** standalone 라우트 경로 */
  href: string;
  /** 좌측 네비·카드 제목 */
  label: string;
  icon: LucideIcon;
  /** 인덱스 카드 한 줄 설명 */
  description: string;
}

export const MY_DASHBOARD_HOME = "/my";
export const MY_DASHBOARD_HOME_LABEL = "대시보드";
export const MY_DASHBOARD_TITLE = "내 메뉴";
export const MY_DASHBOARD_HOME_ICON = Home;

export const MY_NAV_ITEMS: MyNavItem[] = [
  {
    href: "/my/study-progress",
    label: "공부 진행상황",
    icon: BarChart3,
    description: "과목별 학습 진도와 출석 현황을 한눈에 확인해요.",
  },
  {
    href: "/my/schedule",
    label: "학습 일정",
    icon: CalendarDays,
    description: "오늘 할 학습을 캘린더로 관리하고 일정을 조정해요.",
  },
  {
    href: "/my/error-notes",
    label: "나의 오답노트",
    icon: BookOpen,
    description: "저장·오답 문제와 정답률 통계를 다시 풀어봐요.",
  },
  {
    href: "/my/personal-quiz",
    label: "나만의 시험지 Beta",
    icon: FilePenLine,
    description:
      "문제 출처와 조건을 골라 나만의 시험지를 만들어요. Premium 이상 전용이에요.",
  },
  {
    href: "/my/badges",
    label: "도전과제",
    icon: Trophy,
    description: "학습으로 모은 도전과제 배지를 모아봐요.",
  },
  {
    href: "/my/notes",
    label: "필기 모음",
    icon: NotebookPen,
    description: "글마다 작성한 필기를 모아서 복습해요.",
  },
  {
    // JCG-3000 페이지 즐겨찾기 — 다시 볼 이론·코딩 페이지를 별표로 모아 바로 찾아가요.
    href: "/my/bookmarks",
    label: "즐겨찾기",
    icon: Star,
    description: "다시 보고 싶은 이론·코딩 페이지를 과목별로 모아봐요.",
  },
  {
    // JCG-2706 내 댓글 모아보기 — 어디에 댓글을 남겼는지 잊었을 때 원문으로 바로 이동.
    href: "/my/comments",
    label: "내 댓글",
    icon: MessageSquare,
    description: "글마다 남긴 댓글과 답글을 모아 원문으로 바로 찾아가요.",
  },
];

/** 현재 경로가 어떤 /my 탭인지 — 활성 표시용(정확 일치 + 하위 경로 prefix). */
export function isMyNavItemActive(pathname: string, href: string): boolean {
  return pathname === href || pathname.startsWith(href + "/");
}
